/* global axios */
/**
 * The message-wording screens' wire, and the one place in the Waslo tree that talks to
 * CHATWOOT rather than to the authoring service.
 *
 * WHY IT IS NOT IN `api/client.js`. That module is the Waslo AI section's wire to the
 * authoring BFF at `/authoring/api/v1` — a different origin path, a different auth story and a
 * different error vocabulary. A WhatsApp template lives on the Chatwoot channel row (the WABA
 * id and the Graph token are there, and nowhere else), so these three calls go to Chatwoot's
 * own `/api/v1/accounts/:id/**`. Mixing them into one client would mean one module with two
 * error vocabularies, which is how a 402 about AI credits ends up rendered on a screen that
 * has nothing to do with credits.
 *
 * WHY GLOBAL `axios` AND NOT `fetch`, which is what `api/client.js` uses: these are Chatwoot's
 * own routes, and Chatwoot's axios instance is what carries the dashboard's auth headers
 * (`helper/APIHelper.js` sets them on `axios.defaults`). `fetch` would have to reproduce them.
 * Every upstream API module in `dashboard/api/` is written exactly this way.
 *
 * ONE CALL IS UPSTREAM'S, unchanged and deliberately not re-implemented:
 *
 *   REFRESH `POST …/inboxes/:id/sync_templates` → enqueues the platform's own template sync,
 *           which refills `channel.message_templates` — the blob every OTHER consumer in the
 *           product reads. It is background work and it can be refused
 *           (`InboxPolicy#sync_templates?`), so the screen reports what the server said about
 *           the REQUEST rather than claiming the list is now current.
 *
 * THE LIST IS OURS NOW, AND THAT IS THE POINT OF THIS REBUILD. It used to be upstream's
 * `GET …/inboxes/:id/message_templates`, which serves that same stored blob — and the sync that
 * fills it asks Graph with NO `fields` parameter, so the blob structurally cannot carry a
 * quality score, a time-to-live, a sub-category, a previous or correct category, or a library
 * name. A manager screen without those is a list. `GET …/waslo/inboxes/:id/message_templates`
 * asks Meta for each field by name and says which tier of that projection answered.
 *
 * NOTHING IN THIS MODULE DERIVES A STATUS, A CATEGORY OR A QUALITY RATING. Every one of them is
 * null when Meta did not say — see `components/TemplateStatusChip.vue` for what the screen does
 * with a null and with a word Meta has just invented.
 */

export const TEMPLATE_ERROR_CODES = {
  NETWORK: 'network',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  /** The draft was refused — by our own rules or by Meta. `errors` says which field. */
  REJECTED: 'rejected',
  SERVER: 'server',
  MALFORMED: 'malformed',
};

export class TemplateApiError extends Error {
  constructor(
    message,
    { status = 0, code, errors = null, metaError = null } = {}
  ) {
    super(message);
    this.name = 'TemplateApiError';
    this.status = status;
    this.code = code ?? TEMPLATE_ERROR_CODES.SERVER;
    /**
     * `[{ field, code, index? }]` when the server refused the draft, null otherwise. Codes,
     * never sentences: the server states conditions and the form writes the sentence, so an
     * Arabic account never reads an English validation message.
     */
    this.errors = errors;
    /** Meta's own answer when Meta is the one that refused. Null when we refused first. */
    this.metaError = metaError;
  }
}

const codeForStatus = status => {
  if (status === 401) return TEMPLATE_ERROR_CODES.UNAUTHORIZED;
  if (status === 403) return TEMPLATE_ERROR_CODES.FORBIDDEN;
  if (status === 404) return TEMPLATE_ERROR_CODES.NOT_FOUND;
  if (status === 422 || status === 400) return TEMPLATE_ERROR_CODES.REJECTED;
  if (status === 0 || status === undefined) return TEMPLATE_ERROR_CODES.NETWORK;
  return TEMPLATE_ERROR_CODES.SERVER;
};

const wrap = error => {
  if (error instanceof TemplateApiError) return error;
  const status = error?.response?.status ?? 0;
  const data = error?.response?.data ?? {};
  return new TemplateApiError(error?.message ?? 'request failed', {
    status: status,
    code: codeForStatus(status),
    errors: Array.isArray(data.errors) ? data.errors : null,
    metaError: data.meta_error ?? null,
  });
};

const accountBase = accountId => `/api/v1/accounts/${accountId}`;

/**
 * One template, narrowed and NOT defaulted.
 *
 * `status`, `category` and `quality` stay null when Meta's answer does not carry them. That is
 * the whole discipline of this feature: Chatwoot has fabricated the first two before (a
 * `category` regexed out of the template NAME, a `status` that was always "approved"), and an
 * owner shown "approved" for a template Meta has not approved runs a broadcast that fails per
 * recipient. A null reaches the screen as "WhatsApp hasn't said", which is true.
 *
 * `body` is lifted out of `components` for the list row and the search — reading the text in
 * front of us, which is a different act from deriving a verdict nobody gave. `components` rides
 * along whole so `template-facts.js` can read the rest.
 */
const str = value => (typeof value === 'string' && value ? value : null);

const num = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Meta's quality score object, which is `{ score, date }` AND NOTHING ELSE.
 *
 * There is no `reasons` field. Meta's quality page publishes exactly those two keys, and the
 * four "reasons" that circulate on third-party blogs describe a hover state in Meta's own
 * Manager, not an API field. Reading one would render an empty list under a heading promising an
 * explanation.
 */
const narrowQuality = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const score = str(raw.score);
  const date = num(raw.date);
  if (!score && date === null) return null;
  return { score: score, date: date };
};

const narrowTemplate = raw => {
  const components = Array.isArray(raw?.components) ? raw.components : [];
  const body = components.find(
    part => String(part?.type ?? '').toUpperCase() === 'BODY'
  );
  return {
    id: raw?.id ?? null,
    name: typeof raw?.name === 'string' ? raw.name : '',
    language: typeof raw?.language === 'string' ? raw.language : '',
    status: str(raw?.status),
    category: str(raw?.category),
    subCategory: str(raw?.sub_category),
    parameterFormat: str(raw?.parameter_format),
    quality: narrowQuality(raw?.quality_score),
    // Meta sends `rejected_reason` on a refused template, and it is the only place the reason
    // exists. `NONE` is Meta's way of saying "not refused", so it is not a reason.
    rejectedReason:
      typeof raw?.rejected_reason === 'string' && raw.rejected_reason !== 'NONE'
        ? raw.rejected_reason
        : null,
    // What a re-categorisation moved this template away from, and what Meta thinks it should
    // have been. Both change what every send costs, and neither is anywhere else in the product.
    previousCategory: str(raw?.previous_category),
    correctCategory: str(raw?.correct_category),
    messageSendTtlSeconds: num(raw?.message_send_ttl_seconds),
    libraryTemplateName: str(raw?.library_template_name),
    ctaUrlLinkTrackingOptedOut:
      typeof raw?.cta_url_link_tracking_opted_out === 'boolean'
        ? raw.cta_url_link_tracking_opted_out
        : null,
    body: typeof body?.text === 'string' ? body.text : '',
    components: components,
  };
};

/**
 * Meta's per-day points, added up into the four numbers a business actually asks about.
 *
 * EVERY METRIC IS OPTIONAL AND A MISSING ONE IS NOT A ZERO. Meta omits `clicked` entirely on a
 * day with no clicks, and omits any metric it was not asked for — so a total stays null until at
 * least one day carried the key. "WhatsApp sent us nothing" and "it happened zero times" are
 * different sentences on the screen.
 *
 * CLICKS ARE GROUPED BY META'S `type` AND NEVER SUMMED ACROSS TYPES. `url_button` is the raw
 * count and `unique_url_button` is the same taps deduplicated per WhatsApp account, and BOTH can
 * describe one button in one response — adding them together double-counts. Meta's own page also
 * disagrees with itself about which type values exist (its table lists two, its example emits a
 * third), so unknown ones are kept and labelled with Meta's own word rather than dropped.
 */
const narrowAnalytics = raw => {
  const points = Array.isArray(raw.points) ? raw.points : [];
  const total = key =>
    points.reduce(
      (running, point) =>
        typeof point?.[key] === 'number'
          ? (running ?? 0) + point[key]
          : running,
      null
    );
  const clicks = new Map();
  points.forEach(point => {
    (Array.isArray(point?.clicked) ? point.clicked : []).forEach(click => {
      const type = str(click?.type) ?? '';
      const label = str(click?.button_content) ?? '';
      const key = `${type}::${label}`;
      const count = num(click?.count) ?? 0;
      const seen = clicks.get(key);
      if (seen) seen.count += count;
      else clicks.set(key, { type: type, label: label, count: count });
    });
  });
  return {
    days: num(raw.days),
    sent: total('sent'),
    delivered: total('delivered'),
    read: total('read'),
    clicks: [...clicks.values()].sort(
      (left, right) => right.count - left.count
    ),
    // Nothing at all came back for this window — which is a real answer for a template that has
    // not been sent yet, and must not read as a failure.
    empty: points.length === 0,
  };
};

/** Meta's own count and ceiling for this account, or null when this Graph version sent none. */
const narrowSummary = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const used = num(raw.message_template_count ?? raw.total_count);
  const limit = num(raw.message_template_limit);
  if (used === null && limit === null) return null;
  return { used: used, limit: limit };
};

export const templatesApi = {
  /**
   * The WhatsApp numbers this workspace can author wording for.
   *
   * READ FROM CHATWOOT, NOT FROM VUEX. The Waslo tree deliberately holds no store dependency
   * (see `composables/useWasloAccountId.js` for why `useStore()` is avoided here), and a
   * template belongs to a WABA — which is a property of the channel row, not of the assistant.
   * So the inbox list is fetched like everything else on these two screens.
   *
   * ONLY `whatsapp_cloud`. A 360dialog channel is also `Channel::Whatsapp` and has no Graph
   * WABA to submit a template to; the server refuses one, and offering it here would be
   * offering a door that is locked.
   */
  async senders(accountId, signal) {
    try {
      const { data } = await axios.get(`${accountBase(accountId)}/inboxes`, {
        signal: signal,
      });
      const rows = Array.isArray(data?.payload) ? data.payload : null;
      if (!rows) {
        throw new TemplateApiError('payload is not a list', {
          code: TEMPLATE_ERROR_CODES.MALFORMED,
        });
      }
      return rows
        .filter(
          inbox =>
            inbox?.channel_type === 'Channel::Whatsapp' &&
            inbox?.provider === 'whatsapp_cloud'
        )
        .map(inbox => ({
          id: inbox.id,
          name: typeof inbox.name === 'string' ? inbox.name : '',
          phoneNumber:
            typeof inbox.phone_number === 'string' ? inbox.phone_number : '',
        }));
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * The whole library for one WhatsApp number, live from Meta.
   *
   * `meta` is as load-bearing as the rows:
   *   `source`      'whatsapp' when Meta answered, 'stored' when it could not be reached and this
   *                 is the copy the platform last synced. The screen SAYS WHICH — a stale copy
   *                 presented as live is the same class of lie as a fabricated status.
   *   `fields`      which tier of the field list Graph accepted. 'basic' means Meta gave us only
   *                 its default projection, so there is no quality rating to show and the screen
   *                 must say so rather than render a blank.
   *   `truncated`   the library is larger than we walked. A list cut off silently tells an owner
   *                 a template does not exist.
   *   `summary`     Meta's own `{ total_count, message_template_count, message_template_limit }`.
   *                 The limit is READ, never printed: Meta's overview page says up to 6,000 for a
   *                 verified portfolio and its error-code page says 250 full stop.
   */
  async list(accountId, inboxId, signal) {
    try {
      const { data } = await axios.get(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates`,
        { signal: signal }
      );
      if (!Array.isArray(data?.payload)) {
        throw new TemplateApiError('payload is not a list', {
          code: TEMPLATE_ERROR_CODES.MALFORMED,
        });
      }
      const meta = data?.meta ?? {};
      return {
        templates: data.payload.map(narrowTemplate),
        source: meta.source === 'stored' ? 'stored' : 'whatsapp',
        fields: typeof meta.fields === 'string' ? meta.fields : 'basic',
        truncated: meta.truncated === true,
        summary: narrowSummary(meta.summary),
        businessAccountId: str(meta.business_account_id),
        readError: meta.read_error ?? null,
        lastSyncAttemptAt: meta.last_sync_attempt_at ?? null,
      };
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * One template, by Meta's id — the same fields the list saw.
   *
   * `name` is passed when the caller has it (the list row knows), because Meta can then narrow the
   * read server-side and the whole thing is ONE Graph call instead of a walk through the library.
   * It is an optimisation and never a key: the server still selects by id, so a stale name can
   * only fail to find the template, never return a different one.
   */
  async one(accountId, inboxId, templateId, name, signal) {
    try {
      const { data } = await axios.get(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/${templateId}`,
        { params: name ? { name: name } : {}, signal: signal }
      );
      if (!data?.template || typeof data.template !== 'object') {
        throw new TemplateApiError('no template in the answer', {
          code: TEMPLATE_ERROR_CODES.MALFORMED,
        });
      }
      return {
        template: narrowTemplate(data.template),
        fields: typeof data.fields === 'string' ? data.fields : 'basic',
      };
    } catch (error) {
      throw wrap(error);
    }
  },

  /** Asks the platform to go and ask WhatsApp. Resolves with nothing to claim. */
  async requestSync(accountId, inboxId) {
    try {
      await axios.post(
        `${accountBase(accountId)}/inboxes/${inboxId}/sync_templates`
      );
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * Submits one authored template to Meta. Resolves with what META said about it — `status` and
   * `category` may be null, and `category` may differ from `requestedCategory` because Meta
   * re-categorises a template it judges to be marketing, which changes what a send costs.
   */
  async create(accountId, inboxId, wire) {
    try {
      const { data } = await axios.post(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates`,
        { template: wire }
      );
      const template = data?.template;
      if (!template || typeof template !== 'object') {
        throw new TemplateApiError('no template in the answer', {
          code: TEMPLATE_ERROR_CODES.MALFORMED,
        });
      }
      return {
        id: template.id ?? null,
        name: template.name ?? '',
        language: template.language ?? '',
        status: template.status ?? null,
        category: template.category ?? null,
        requestedCategory: template.requested_category ?? null,
      };
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * Changes one template Meta already holds.
   *
   * RESOLVES WITH NOTHING, and that is not an oversight: Meta answers `{ success: true }` and
   * says NOTHING about the template's new status. Returning an invented one — 'PENDING', or
   * worse the status it had a second ago — is the exact defect this whole feature was built
   * against. The screen tells the owner the change was sent and where WhatsApp's verdict will
   * appear.
   */
  async update(accountId, inboxId, templateId, wire) {
    try {
      await axios.patch(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/${templateId}`,
        { template: wire }
      );
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * Removes one template, or every language of its name. IRREVERSIBLE at Meta.
   *
   * `scope` is required rather than defaulted, because the two are not variations of one act:
   * Meta deletes EVERY LANGUAGE of a name when the request carries only the name. An owner with
   * Arabic and English wording under one name, who meant to remove the English, is ruined by the
   * wrong default — so there is no default.
   */
  async remove(accountId, inboxId, templateId, { name, scope }) {
    try {
      await axios.delete(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/${templateId}`,
        { params: { name: name, scope: scope } }
      );
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * How one template actually performed — sent, delivered, read, and which buttons were tapped.
   *
   * THE WINDOW DEFAULTS TO SEVEN DAYS AND THAT IS NOT A ROUND NUMBER. Meta keeps read and click
   * counts for seven days from the send and then RESETS THEM TO ZERO. A 30-day read rate would
   * therefore report zero reads for messages that were read — the one window where all four
   * numbers mean what they look like is this one.
   *
   * A refusal carries Meta's own code through `metaError`, because two of them mean opposite
   * things: 200007 is "not switched on yet" and she can fix it; 200005 is "not available for this
   * account" and she cannot.
   */
  async analytics(accountId, inboxId, templateId, days) {
    try {
      const { data } = await axios.get(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/${templateId}/analytics`,
        { params: days ? { days: days } : {} }
      );
      const given = data?.analytics;
      if (!given || typeof given !== 'object') {
        throw new TemplateApiError('no analytics in the answer', {
          code: TEMPLATE_ERROR_CODES.MALFORMED,
        });
      }
      return narrowAnalytics(given);
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * Switches template analytics on for the whole business account. IRREVERSIBLE at Meta, and it
   * carries a consent statement made on the business's behalf — the screen shows Meta's own
   * wording before this is ever called.
   */
  async enableInsights(accountId, inboxId) {
    try {
      await axios.post(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/insights`
      );
    } catch (error) {
      throw wrap(error);
    }
  },

  /** Lifts a pause WhatsApp will not lift by itself. */
  async unpause(accountId, inboxId, templateId) {
    try {
      await axios.post(
        `${accountBase(accountId)}/waslo/inboxes/${inboxId}/message_templates/${templateId}/unpause`
      );
    } catch (error) {
      throw wrap(error);
    }
  },
};

export default templatesApi;
